import { useMemo, useState } from "react";
import { getThemeClasses } from "../../utils/ThemeClasses";
import { useDailyHistoryMovements } from "../../features/dashboards/queries";
import { useReportingSummary } from "../../features/dashboards/reporting";
import { todayMxISO } from "../../features/dashboards/dailyHistoryAgg";
import {
  platformLabelOf,
  type ReportingPlatform,
} from "../../features/dashboards/reportingConfig";
import DeviceMovementsModal from "./DeviceMovementsModal";

const ALL_PLATFORMS: ReportingPlatform[] = ["wialon", "mapon", "v5", "unattributed"];

type ModalState = { platform: ReportingPlatform | null; type: "alta" | "baja" };

export default function AltasBajasPlatformCards({
  themeClasses,
  platform,
}: {
  themeClasses: ReturnType<typeof getThemeClasses>;
  platform?: ReportingPlatform;
}) {
  const summaryQuery = useReportingSummary();
  const summary = summaryQuery.data;
  const loading = summaryQuery.isLoading;

  const today = todayMxISO();
  const movementsQuery = useDailyHistoryMovements({ from: today, to: today });
  const devices = useMemo(() => movementsQuery.data ?? [], [movementsQuery.data]);

  const [modal, setModal] = useState<ModalState | null>(null);

  const count = (p: ReportingPlatform | null, type: "alta" | "baja") => {
    const src = p ? summary?.by_platform?.[p] : summary;
    return (type === "alta" ? src?.altas_count : src?.bajas_count) ?? 0;
  };

  const numBtn = (value: number, kind: "alta" | "baja", onClick: () => void) => {
    const isAlta = kind === "alta";
    const clickable = !loading && value > 0;
    return (
      <button
        type="button"
        disabled={!clickable}
        onClick={onClick}
        title={clickable ? "Ver dispositivos" : undefined}
        className={`flex items-center gap-1.5 font-bold ${isAlta ? "text-green-500" : "text-red-500"} disabled:opacity-60 disabled:cursor-default enabled:cursor-pointer enabled:hover:underline`}
      >
        <i className={isAlta ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
        <span className="text-lg sm:text-2xl">{loading ? "…" : value}</span>
        {clickable && <i className="ri-eye-line text-xs opacity-70" />}
      </button>
    );
  };

  const card = (label: string, p: ReportingPlatform | null) => (
    <div
      key={label}
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4`}
    >
      <p className={`text-xs font-medium ${themeClasses.textSecondary} mb-2 truncate`}>
        {label}
      </p>
      <div className="flex items-center justify-between gap-2">
        {numBtn(count(p, "alta"), "alta", () => setModal({ platform: p, type: "alta" }))}
        {numBtn(count(p, "baja"), "baja", () => setModal({ platform: p, type: "baja" }))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Altas y bajas por plataforma
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Movimiento de unidades en vivo — click en un número para ver los equipos
          </p>
        </div>
        <span
          title="Dato en vivo de reporting/summary (últimas 24 h). El detalle de equipos sale del historial diario."
          className={`text-[11px] whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
        >
          <i className="ri-broadcast-line" />
          En vivo · últimas 24 h
        </span>
      </div>

      <div
        className={`grid gap-3 sm:gap-4 ${platform ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-5"}`}
      >
        {!platform && card("Total", null)}
        {(platform ? [platform] : ALL_PLATFORMS).map((p) =>
          card(platformLabelOf(p), p),
        )}
      </div>

      {modal && (
        <DeviceMovementsModal
          themeClasses={themeClasses}
          title={`${modal.platform ? platformLabelOf(modal.platform) : "Todas"} · ${modal.type === "alta" ? "Altas" : "Bajas"}`}
          devices={devices.filter(
            (d) =>
              (modal.platform ? d.platform === modal.platform : true) &&
              d.type === modal.type,
          )}
          loading={movementsQuery.isLoading}
          showType={false}
          showPlatform={!modal.platform}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
